/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { formatDate, formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  Card,
  CbhIcon,
  Divider,
  DotSeparatedList,
  Illustration,
  Pill,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, formatDistanceToNow } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { zonedTimeToUtc } from "date-fns-tz";
import { isEmpty } from "lodash";
import Confetti from "react-confetti";

import {
  INTERVIEW_TYPE,
  type InterviewType,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import { type PlacementDetail } from "../api/useGetPlacement";
import { HIRING_TEXTING_PHONE_NUMBER } from "../constants";
import { useAddInterviewToCalendar } from "../hooks/useAddInterviewToCalendar";
import { AddToCalendarCard } from "./AddToCalendarCard";
import { BrowseMoreJobsButton } from "./BrowseMoreJobsButton";
import { BrowseMyJobsButton } from "./BrowseMyJobs";

interface InterviewBookedDialogProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
  selectedInterviewTime: {
    start: Date;
    end: Date;
  };
  interviewType?: InterviewType;
  interviewId?: string;
  icsFileUrl?: string;
  workplaceStartHour?: Date;
  isRecruiterScreeningEnabled: boolean;
  onConfirm?: () => void;
}

export function InterviewBookedDialog(props: InterviewBookedDialogProps) {
  const {
    modalState,
    placementDetails,
    selectedInterviewTime,
    interviewType,
    interviewId,
    icsFileUrl,
    workplaceStartHour,
    isRecruiterScreeningEnabled,
    onConfirm,
  } = props;
  const worker = useDefinedWorker();
  const { addToCalendar, isAddingToCalendar } = useAddInterviewToCalendar();
  const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);

  // Use the actual interview type if provided, otherwise fall back to workplace settings
  const isPhoneScreen =
    interviewType === INTERVIEW_TYPE.PHONE_SCREEN ||
    (!interviewType &&
      placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
        INTERVIEW_TYPE.PHONE_SCREEN);

  const primaryHiringManager = placementDetails.hiringManagers?.find(
    (manager) => manager.isPrimary
  );

  const trimmedName = primaryHiringManager?.attributes.name?.trim();
  const trimmedFirstName = primaryHiringManager?.attributes.firstName?.trim();
  const hiringManagerDisplayName = isEmpty(trimmedName) ? trimmedFirstName : trimmedName;

  function getTimeOfDayIcon(date: Date): "sun-filled" | "sunset-filled" {
    const hour = date.getHours();
    // Consider daytime between 6 AM and 12 PM
    return hour >= 6 && hour < 12 ? "sun-filled" : "sunset-filled";
  }

  const interviewWorkplaceDetails = [
    isDefined(placementDetails.workerTypes) && (
      <Text key="shift-types" variant="body2">
        {placementDetails.workerTypes?.join(", ")}
      </Text>
    ),
    <Text key="workplace-name" variant="body2">
      {placementDetails.workplace.name}
    </Text>,
    isDefined(placementDetails.distance) && (
      <Text key="distance" variant="body2">
        {formatDistanceInMiles(placementDetails.distance)}
      </Text>
    ),
  ].filter(Boolean);

  const handleClose = () => {
    modalState.closeModal();
  };

  // Using href on Button (renders as anchor) instead of Browser.open() which doesn't support sms: URLs
  const formattedInterviewDate = formatDate(selectedInterviewTime.start, {
    timeZone: worker.tmz,
  });
  const smsMessage = `Hey! I just booked an interview at ${placementDetails.workplace.name} on ${formattedInterviewDate} and had some questions about the job and interview process. Could you help me out?`;
  const recruiterSmsUrl = `sms:${HIRING_TEXTING_PHONE_NUMBER}?body=${encodeURIComponent(
    smsMessage
  )}`;

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        isDefined(onConfirm) ? (
          <DialogFooter orientation="vertical">
            <Button fullWidth variant="contained" size="large" onClick={onConfirm}>
              Got it
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter orientation="vertical">
            <BrowseMoreJobsButton />
            {isPhoneScreen || !isRecruiterScreeningEnabled ? (
              <BrowseMyJobsButton
                applicationMethod={placementDetails.applicationMethod}
                buttonText="View my application"
                logMetadata={{
                  workplaceId: placementDetails.workplace.id,
                  placementId: placementDetails.id,
                  buttonSource: "placement_details_page",
                }}
              />
            ) : (
              <Button fullWidth variant="outlined" size="large" href={recruiterSmsUrl}>
                Need help? Message a recruiter
              </Button>
            )}
          </DialogFooter>
        )
      }
    >
      <Box sx={{ px: 5, py: 6 }}>
        <Confetti recycle={false} />
        <Stack spacing={7} alignItems="center" justifyContent="center">
          <Illustration type="shift-booked" />
          <Stack spacing={7} sx={{ px: 10 }}>
            <Text variant="h2" textAlign="center">
              {isPhoneScreen
                ? "Your Phone Call is booked"
                : `Your ${isProspectWorkplace ? "Walk-In" : "Interview"} is booked!`}
            </Text>
            {isPhoneScreen && (
              <Text variant="body1" textAlign="center">
                Please make sure to answer the call during the scheduled day and time!
              </Text>
            )}
          </Stack>

          <Card outlined variant="tertiary" elevation={2} sx={{ width: "100%" }}>
            <CardContent>
              <Stack spacing={6}>
                <Pill
                  size="medium"
                  sx={{ width: "fit-content" }}
                  label={
                    <DotSeparatedList>
                      {format(selectedInterviewTime.start, "EEE, MMM d")}
                      {`Starts ${formatDistanceToNow(
                        zonedTimeToUtc(selectedInterviewTime.start, worker.tmz),
                        {
                          addSuffix: true,
                        }
                      )}`}
                    </DotSeparatedList>
                  }
                />
                <Stack direction="row" spacing={4}>
                  <Stack alignItems="center" sx={{ pt: 1 }}>
                    <CbhIcon type={getTimeOfDayIcon(selectedInterviewTime.start)} />
                  </Stack>
                  <Stack spacing={2}>
                    <Text semibold variant="h4">
                      {`${format(selectedInterviewTime.start, "h:mm aaa")} - ${format(
                        selectedInterviewTime.end,
                        "h:mm aaa"
                      )}`}
                    </Text>
                    <DotSeparatedList>{interviewWorkplaceDetails}</DotSeparatedList>
                  </Stack>
                </Stack>
                {isDefined(interviewId) && isDefined(icsFileUrl) && (
                  <AddToCalendarCard
                    disabled={isAddingToCalendar}
                    onClick={async () => {
                      await addToCalendar({
                        placementId: placementDetails.id,
                        interviewId,
                        interviewType:
                          interviewType ??
                          placementDetails.interviewSettings?.attributes
                            .selfServeInterviewBookingType ??
                          INTERVIEW_TYPE.IN_PERSON,
                        icsFileUrl,
                      });
                    }}
                  />
                )}
                {isPhoneScreen ? undefined : (
                  <>
                    <Divider />
                    <Stack spacing={4} sx={{ width: "100%" }}>
                      <Text semibold variant="h6">
                        What to expect next
                      </Text>
                      <Stack spacing={3}>
                        <Stack direction="row" spacing={3} alignItems="flex-start">
                          <Box sx={{ mt: 1 }}>
                            <CbhIcon
                              type="dot"
                              size="xSmall"
                              color={(theme) => theme.palette.text.secondary}
                            />
                          </Box>
                          <Text variant="body2">
                            We&apos;ve notified {placementDetails.workplace.name} of your interview
                            booking.
                          </Text>
                        </Stack>
                        {isDefined(workplaceStartHour) ? (
                          <Stack direction="row" spacing={3} alignItems="flex-start">
                            <Box sx={{ mt: 1 }}>
                              <CbhIcon
                                type="dot"
                                size="xSmall"
                                color={(theme) => theme.palette.text.secondary}
                              />
                            </Box>
                            <Text variant="body2">
                              You have until {formatTime(workplaceStartHour)} on{" "}
                              {formatDate(workplaceStartHour)} to cancel or reschedule this
                              interview.
                            </Text>
                          </Stack>
                        ) : undefined}

                        {hiringManagerDisplayName ? (
                          <Stack direction="row" spacing={3} alignItems="flex-start">
                            <Box sx={{ mt: 1 }}>
                              <CbhIcon
                                type="dot"
                                size="xSmall"
                                color={(theme) => theme.palette.text.secondary}
                              />
                            </Box>
                            <Text variant="body2">
                              Your interview will be with {hiringManagerDisplayName}
                              {primaryHiringManager?.attributes.jobTitle
                                ? `, the ${primaryHiringManager.attributes.jobTitle}`
                                : undefined}
                              {" at "}
                              {placementDetails.workplace.name}.
                            </Text>
                          </Stack>
                        ) : undefined}
                      </Stack>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
